import React, { Component } from 'react';
import { isEmpty, isEqual, debounce } from 'lodash-es';
import { Input, Autocomplete, Alert } from 'components/antd';
import { GoogleAddress } from 'app-types';
import { SelectValue } from 'antd/lib/select';
import NotFound from 'components/LocationInput/NotFound';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { ListAlert } from 'components';
import { FILTERING_THROTTLE_INTERVAL } from 'app-constants';

const FE_VALIDATION_ERROR = 'address not found, select from the suggested list to continue';

interface Props {
	addressesSuggestions?: GoogleAddress[];
	form: WrappedFormUtils;
	fieldName: string;
	initialValue?: GoogleAddress;
	required?: boolean;
	disabled?: boolean;
	onStringAddressChange: (address: string) => void;
	validateGoogleAddress: (placeId: string, isFEValidationError?: boolean) => void;
	onClear?: () => void;
	validationAddressErrors?: string[];
	focusOnStart?: boolean;
	className?: string;
	isAddressLoading: boolean;
	isValidateAddressLoading?: boolean;
}

interface State {
	isFEValidationError: boolean;
}

export default class LocationInput extends Component<Props, State> {
	state = { isFEValidationError: false };
	notSelectedAddress: boolean = true;
	isChanged?: boolean;

	private inputRef = React.createRef<Input>();

	componentWillUnmount() {
		this.resetValidationErrors();
	}

	componentDidMount(): void {
		if (this.props.focusOnStart) {
			const { current } = this.inputRef;
			if (current) {
				current.focus();
			}
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { validationAddressErrors, fieldName, required } = this.props;
		if (
			!isEqual(validationAddressErrors, prevProps.validationAddressErrors) ||
			required !== prevProps.required
		) {
			this.props.form.validateFields([fieldName], { force: true });
		}
	}

	resetValidationErrors(value?: SelectValue) {
		if (!value && this.props.onClear) {
			this.props.onClear();
			this.setState({
				isFEValidationError: false,
			});
		}
	}

	handleGoogleAddressSearch = (value: SelectValue) => {
		this.isChanged = true;
		this.props.onStringAddressChange(value as string);

		this.resetValidationErrors(value);
	};

	handleGoogleAddressSearchDebounce = debounce(
		this.handleGoogleAddressSearch,
		FILTERING_THROTTLE_INTERVAL
	);

	validateGoogleAddress = (value: SelectValue) => {
		this.isChanged = false;
		this.props.validateGoogleAddress(value as string);
		this.setState({
			isFEValidationError: false,
		});
	};

	onBlur = (value: SelectValue) => {
		if (value && this.isChanged) {
			this.setState({
				isFEValidationError: true,
			});
		}
	};

	getDataSource = () => {
		const {
			addressesSuggestions = [],
			initialValue = {} as GoogleAddress,
			isAddressLoading,
		} = this.props;

		const addrs =
			addressesSuggestions !== null
				? initialValue
					? addressesSuggestions.filter(item => item.placeId !== initialValue.placeId)
					: addressesSuggestions
				: [];

		return addrs
			.concat(!isEmpty(initialValue) && !isAddressLoading ? [initialValue] : [])
			.map(item => {
				return {
					value: item.placeId,
					text: `${item.fullAddress} (${item.name})`,
				};
			});
	};

	getAlertsInfo = () => {
		let alerts = [];
		const { validationAddressErrors } = this.props;

		if (this.state.isFEValidationError) {
			alerts.push({
				messages: [FE_VALIDATION_ERROR],
				listHeader: 'Address:',
			});
		}

		if ((validationAddressErrors || []).length) {
			alerts.push({
				messages: validationAddressErrors,
				listHeader: 'Address:',
			});
		}

		return alerts;
	};

	isAddressValid = () => {
		const { validationAddressErrors } = this.props;

		return (validationAddressErrors || []).length === 0;
	};

	render() {
		const {
			fieldName,
			initialValue,
			form,
			required,
			disabled,
			className,
			addressesSuggestions,
			isAddressLoading,
			isValidateAddressLoading,
		} = this.props;

		const alerts = this.getAlertsInfo();

		const notFoundContent = (
			<NotFound
				isAddressLoading={isAddressLoading}
				addressesSuggestions={addressesSuggestions}
			/>
		);

		return (
			<div id="googleAddressInput" className={className}>
				{form.getFieldDecorator(fieldName, {
					initialValue: initialValue && initialValue.placeId,
					rules: [
						{
							validator: (_rule: unknown, value: string, callback) => {
								const checkEmptyValue = required ? value : true;
								const checkChanged = required
									? !this.isChanged
									: !this.isChanged || (this.isChanged && !value);

								callback(
									checkEmptyValue && this.isAddressValid() && checkChanged
										? undefined
										: 'Incorrect address'
								);
							},
							whitespace: true,
						},
					],
				})(
					<Autocomplete
						allowClear
						notFoundContent={notFoundContent}
						getPopupContainer={() =>
							document.getElementById('googleAddressInput') || document.body
						}
						dataSource={this.getDataSource()}
						onSelect={this.validateGoogleAddress}
						onSearch={this.handleGoogleAddressSearchDebounce}
						onBlur={this.onBlur}
						disabled={disabled}
						isValid={this.isAddressValid()}
						isLoading={isValidateAddressLoading}
					/>
				)}
				{alerts.length > 0 &&
					alerts.map((item, index) => {
						const message = (
							<ListAlert errors={item.messages || []} listHeader={item.listHeader} />
						);
						return (
							<Alert
								type="error"
								message={message}
								showIcon
								key={`${item.listHeader}-${index}`}
							/>
						);
					})}
			</div>
		);
	}
}
